<template>
  <v-container fluid>
    <v-dialog v-model="submitted" persistent width="300">
      <v-card>
        <v-card-text v-if="submitting">
          <h2 class="title mb-4 pt-4">Creating New Job...</h2>
          <v-progress-linear
            indeterminate
            class="mb-0 mt-4"
          ></v-progress-linear>
        </v-card-text>
        <v-card-text v-else class="text-center">
          <h2 class="title mb-4 pt-4">Job successfully created!</h2>
          <v-btn color="primary" outlined to="/jobs"
            ><v-icon left>chevron_left</v-icon> Back to jobs</v-btn
          >
          <div>
            <v-btn text @click="submitted = false" class="mt-4">close</v-btn>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-row wrap>
      <v-col xs="12" class="headline">
        <v-btn
          color="primary"
          class="mr-2"
          small
          outlined
          text
          to="/jobs"
          style="margin-top:-3px"
          ><v-icon>chevron_left</v-icon></v-btn
        >
        New Job Form
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" md="12" lg="6">
        <v-card>
          <v-card-text>
            <v-form ref="form" v-model="valid" lazy-validation>
              <v-container>
                <v-row class="mb-4">
                  <v-col cols="12" xs="12" sm="6">
                    <v-autocomplete
                      v-model="estimator"
                      :items="pmList"
                      attach
                      label="Estimator"
                      return-object
                      item-text="name"
                      clearable
                    />
                  </v-col>
                  <v-col cols="12" xs="12" sm="6">
                    <v-dialog
                      ref="dateAwardedRef"
                      v-model="dialogDateAwarded"
                      :return-value.sync="dateAwarded"
                      persistent
                      width="290px"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          :value="computedDateFormattedMomentjsAwarded"
                          label="Date Awarded"
                          readonly
                          v-bind="attrs"
                          v-on="on"
                        ></v-text-field>
                      </template>
                      <v-date-picker v-model="dateAwarded" scrollable>
                        <v-spacer></v-spacer>
                        <v-btn
                          text
                          color="primary"
                          @click="dialogDateAwarded = false"
                          >Cancel</v-btn
                        >
                        <v-btn
                          text
                          color="primary"
                          @click="$refs.dateAwardedRef.save(dateAwarded)"
                          >OK</v-btn
                        >
                      </v-date-picker>
                    </v-dialog>
                  </v-col>
                  <v-col cols="12">
                    <v-text-field
                      v-model.trim="street"
                      label="Street Address"
                      required
                      :rules="[(v) => !!v || 'Street is required']"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12">
                    <v-text-field
                      v-model.trim="street2"
                      label="Street Address Line 2"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" xs="12" sm="5">
                    <v-text-field
                      v-model.trim="city"
                      label="City"
                      required
                      :rules="[(v) => !!v || 'City is required']"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" xs="12" sm="3">
                    <v-select
                      v-model="state"
                      :items="statesList"
                      label="State"
                      required
                      :rules="[(v) => !!v || 'State is required']"
                    ></v-select>
                  </v-col>
                  <v-col cols="12" xs="12" sm="4">
                    <v-text-field
                      v-model.trim="postalCode"
                      label="Zip Code"
                      required
                      :rules="[(v) => !!v || 'Zip Code is required']"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12">
                    <v-alert type="info" outlined dense class="mb-0">
                      <div class="d-flex align-center justify-space-between">
                        <span>Find coordinates using the address above</span>
                        <v-btn
                          color="primary"
                          small
                          :loading="findingCoordinates"
                          :disabled="
                            !street ||
                              !city ||
                              !state ||
                              !postalCode ||
                              !!foundCoordinates
                          "
                          @click="findCoordinates"
                        >
                          <v-icon small left>location_searching</v-icon>
                          Find Coordinates
                        </v-btn>
                      </div>
                    </v-alert>
                    <v-alert
                      v-if="coordinateSearchAttempted && !foundCoordinates"
                      type="error"
                      dense
                      class="mt-2"
                    >
                      <div>
                        <strong
                          >Unable to find coordinates for this address</strong
                        >
                        <div class="caption mt-1">
                          Searched for: {{ searchedAddress }}
                        </div>
                        <div class="caption mt-1">
                          Please verify the address is correct and try again.
                          The job will be created without coordinates if you
                          proceed.
                        </div>
                      </div>
                    </v-alert>
                  </v-col>
                  <v-col cols="12" xs="12" sm="6" v-if="foundCoordinates">
                    <v-text-field
                      :value="foundCoordinates.latitude"
                      label="Latitude"
                      readonly
                      prepend-icon="place"
                      outlined
                      dense
                      success
                    >
                      <template v-slot:append-outer>
                        <v-btn
                          icon
                          small
                          @click="clearCoordinates"
                          color="error"
                        >
                          <v-icon small>close</v-icon>
                        </v-btn>
                      </template>
                    </v-text-field>
                  </v-col>
                  <v-col cols="12" xs="12" sm="6" v-if="foundCoordinates">
                    <v-text-field
                      :value="foundCoordinates.longitude"
                      label="Longitude"
                      readonly
                      prepend-icon="place"
                      outlined
                      dense
                      success
                    ></v-text-field>
                    <div class="caption text--secondary mt-n2">
                      Searched: {{ searchedAddress }}
                    </div>
                  </v-col>
                  <v-col cols="12" xs="12" sm="6">
                    <v-text-field
                      v-model.trim="jobId"
                      label="Job Id (example: 20-2347)"
                      required
                      :rules="[(v) => !!v || 'Job Number is required']"
                    ></v-text-field>
                    <div class="red--text" v-if="dupeJobIdCheck(jobId)">
                      * WARNING: Job Id already exists (you can still create the
                      job)
                    </div>
                  </v-col>
                  <v-col cols="12" xs="12" sm="6">
                    <v-text-field
                      v-model.trim="proposalNumber"
                      label="Proposal Number"
                    ></v-text-field>
                  </v-col>
                  <v-col
                    cols="12"
                    xs="12"
                    sm="6"
                    v-if="!isExternalTruckRequest"
                  >
                    <v-select
                      v-model="entity"
                      :items="['Volk', 'Alba Services']"
                      label="Entity"
                      required
                      :rules="[(v) => !!v || 'Entity is required']"
                    ></v-select>
                  </v-col>
                  <v-col cols="12" xs="12">
                    <v-select
                      v-model="department"
                      chips
                      :items="[
                        'Demolition',
                        'Core & Shell',
                        'Concrete & Steel',
                        'Asbestos',
                        'Electrical',
                        'T&M',
                        'External Truck Request',
                        'Fireproofing',
                        'Superstructure',
                      ]"
                      label="Choose department(s)"
                      :rules="[(v) => !!v.length || 'Department is required']"
                      multiple
                      @change="onDepartmentChange"
                    ></v-select>
                  </v-col>
                  <v-col cols="12" xs="12" v-if="isExternalTruckRequest">
                    <v-row dense>
                      <v-col cols="12">
                        <v-autocomplete
                          v-model="client"
                          :items="clients"
                          item-text="name"
                          dense
                          return-object
                          label="Select Client"
                          :rules="[
                            (v) =>
                              !isExternalTruckRequest ||
                              !!v ||
                              'Client is required for External Truck Request',
                          ]"
                          required
                          :loading="loadingClients"
                          outlined
                        >
                          <template v-slot:append-outer>
                            <v-btn
                              color="primary"
                              outlined
                              to="/clients"
                              target="_blank"
                              style="margin-top: -6px"
                            >
                              <v-icon small left>add</v-icon>
                              Create Client
                            </v-btn>
                          </template>
                        </v-autocomplete>
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>
                <v-row class="mb-4">
                  <v-col cols="12" class="mt-4">
                    <h4>Project Managers</h4>
                    <span class="caption text--grey"
                      >Select project managers for this job and setup email
                      notifications</span
                    >
                    <v-autocomplete
                      @change="updatePmList"
                      v-model="projectManagers"
                      :items="pmList"
                      attach
                      chips
                      label="Select PM's"
                      multiple
                      return-object
                      item-text="name"
                      deletable-chips
                    >
                    </v-autocomplete>
                    <p v-if="projectManagers.length" class="mb-2">
                      <v-icon small>mail_outline</v-icon> Email Notifications
                      Settings
                    </p>
                    <v-card
                      v-if="projectManagers.length"
                      color="#f1f1f1"
                      elevation="1"
                    >
                      <v-simple-table dense>
                        <template v-slot:default>
                          <thead>
                            <tr>
                              <th class="text-left"></th>
                              <th class="text-left">Tool Orders</th>
                              <th class="text-left">Tool Pickups</th>
                              <th class="text-left">
                                Consumables Budget Email
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="pm in projectManagers" :key="pm.email">
                              <td>{{ pm.name }}</td>
                              <td class="align-center">
                                <v-switch
                                  v-model="pm.notifications.toolOrders"
                                  color="green"
                                ></v-switch>
                              </td>
                              <td class="align-center">
                                <v-switch
                                  v-model="pm.notifications.toolPickups"
                                  color="green"
                                ></v-switch>
                              </td>
                              <td class="align-center">
                                <v-switch
                                  v-model="pm.notifications.consumablesBudget"
                                  color="green"
                                ></v-switch>
                              </td>
                            </tr>
                          </tbody>
                        </template>
                      </v-simple-table>
                    </v-card>
                  </v-col>
                </v-row>
                <v-row class="mb-4 mt-4 pt-4">
                  <v-col cols="12">
                    <h4>Scope & Budgets</h4>
                    <div class="caption text--grey mb-4">
                      Enter budget amounts. Entering a consumables budget amount
                      will send an email alert once that amount is reached.
                      Example: If the budget is 10K you may want to be alerted
                      when it reaches 8K.
                    </div>
                  </v-col>
                  <v-col cols="12" xs="12" sm="6">
                    <v-text-field
                      label="Contract Value"
                      v-model.number="contractValue"
                      persistent-hint
                      :hint="contractValue | currency"
                      prepend-icon="monetization_on"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" xs="12" sm="6">
                    <v-text-field
                      label="Scope Breakdown Notes"
                      v-model="scopeBreakdownNote"
                      prepend-icon="note"
                    ></v-text-field>
                  </v-col>

                  <v-col cols="12" xs="12" sm="6">
                    <v-text-field
                      label="Total Estimated Budget"
                      v-model.number="totalBudget"
                      persistent-hint
                      :hint="totalBudget | currency"
                      prepend-icon="monetization_on"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" xs="12" sm="6">
                    <v-text-field
                      label="Estimated Budget Notes"
                      v-model="totalBudgetNote"
                      prepend-icon="note"
                    ></v-text-field>
                  </v-col>

                  <v-col cols="12" xs="12" sm="6">
                    <v-text-field
                      label="Estimated Budget - LABOR"
                      v-model.number="laborBudget"
                      persistent-hint
                      :hint="laborBudget | currency"
                      prepend-icon="monetization_on"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" xs="12" sm="6">
                    <v-text-field
                      label="Man Days"
                      v-model.number="manDays"
                      type="number"
                      prepend-icon="person"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" xs="12" sm="12">
                    <v-text-field
                      label="Labor Budget Notes"
                      v-model="laborBudgetNote"
                      prepend-icon="note"
                    ></v-text-field>
                  </v-col>

                  <v-col cols="12" xs="12" sm="6">
                    <v-text-field
                      label="Estimated Budget - DUMPING"
                      v-model.number="dumpingBudget"
                      persistent-hint
                      :hint="dumpingBudget | currency"
                      prepend-icon="monetization_on"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" xs="12" sm="6">
                    <v-text-field
                      label="Dumping Budget Notes"
                      v-model="dumpingBudgetNote"
                      prepend-icon="note"
                    ></v-text-field>
                  </v-col>

                  <v-col cols="12" xs="12" sm="6">
                    <v-text-field
                      label="Estimated Budget - MATERIAL"
                      v-model.number="materialBudget"
                      persistent-hint
                      :hint="materialBudget | currency"
                      prepend-icon="monetization_on"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" xs="12" sm="6">
                    <v-text-field
                      label="Material Budget Notes"
                      v-model="materialBudgetNote"
                      prepend-icon="note"
                    ></v-text-field>
                  </v-col>

                  <v-col cols="12" xs="12" sm="6">
                    <v-text-field
                      label="Estimated Budget - CONSUMABLES"
                      v-model.number="consumablesBudget"
                      persistent-hint
                      :hint="consumablesBudget | currency"
                      prepend-icon="monetization_on"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" xs="12" sm="6">
                    <v-text-field
                      label="Consumables Budget - Alert Amount"
                      v-model.number="consumablesBudgetAlert"
                      persistent-hint
                      :hint="consumablesBudgetAlert | currency"
                      prepend-icon="notifications"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" xs="12" sm="6">
                    <v-text-field
                      label="Subcontractor Value"
                      v-model.number="subcontractorValue"
                      persistent-hint
                      :hint="subcontractorValue | currency"
                      prepend-icon="monetization_on"
                    ></v-text-field>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" xs="12" sm="6">
                    <v-dialog
                      ref="dialogStart"
                      v-model="dialogStart"
                      :return-value.sync="estimatedStartDate"
                      persistent
                      width="290px"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          :value="computedDateFormattedMomentjsStart"
                          label="Estimated Start Date"
                          readonly
                          v-bind="attrs"
                          v-on="on"
                          prepend-icon="today"
                        ></v-text-field>
                      </template>
                      <v-date-picker v-model="estimatedStartDate" scrollable>
                        <v-spacer></v-spacer>
                        <v-btn text color="primary" @click="dialogStart = false"
                          >Cancel</v-btn
                        >
                        <v-btn
                          text
                          color="primary"
                          @click="$refs.dialogStart.save(estimatedStartDate)"
                          >OK</v-btn
                        >
                      </v-date-picker>
                    </v-dialog>
                  </v-col>
                  <v-col cols="12" xs="12" sm="6">
                    <v-dialog
                      ref="dialogEnd"
                      v-model="dialogEnd"
                      :return-value.sync="estimatedCompletionDate"
                      persistent
                      width="290px"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          :value="computedDateFormattedMomentjsEnd"
                          label="Estimated Completion Date"
                          readonly
                          v-bind="attrs"
                          v-on="on"
                          prepend-icon="event_available"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="estimatedCompletionDate"
                        scrollable
                      >
                        <v-spacer></v-spacer>
                        <v-btn text color="primary" @click="dialogEnd = false"
                          >Cancel</v-btn
                        >
                        <v-btn
                          text
                          color="primary"
                          @click="$refs.dialogEnd.save(estimatedCompletionDate)"
                          >OK</v-btn
                        >
                      </v-date-picker>
                    </v-dialog>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" class="">
                    <p class="text--grey">
                      If turned on, anyone punching in on this jobsite will be
                      excluded from the payroll lunch rule.
                    </p>
                    <v-switch
                      v-model="ignoreLunchRule"
                      color="green"
                      label="Ignore Payroll Lunch Rule"
                    ></v-switch>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" class="px-0">
                    <div class="mb-4 mt-4">
                      <v-alert type="info" outlined dense>
                        Please make sure the job
                        <strong>does NOT already exist</strong> before you
                        create it!
                      </v-alert>
                    </div>
                    <div class="text-right mt-4 pt-4">
                      <span v-if="!valid" class="red--text pr-4">
                        Missing some information. Please check above.
                      </span>
                      <v-btn
                        :disabled="!valid || submitting"
                        color="primary"
                        class="mr-4"
                        large
                        :loading="submitting"
                        @click="addJob"
                        >Create JOb</v-btn
                      >
                    </div>
                    <br />
                    <br />
                  </v-col>
                </v-row>
              </v-container>
            </v-form>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-snackbar v-model="snack" :timeout="3000" :color="snackColor">
      {{ snackText }}
      <v-btn text @click="snack = false">Close</v-btn>
    </v-snackbar>
  </v-container>
</template>

<script>
import utils from "@/helpers/utils";
import fb from "@/main";
import { mapState } from "vuex";
import moment from "moment";
import firebase from "firebase/app";

export default {
  data() {
    return {
      // helpers
      statesList: utils.statesListShort,
      valid: true,
      pmList: [],
      dialogDateAwarded: false,
      dialogStart: false,
      dialogEnd: false,
      // form
      street: "",
      street2: "",
      city: "",
      state: "",
      postalCode: "",
      proposalNumber: "",
      jobId: "",
      // Volk is the operating entity now, ALBA only on a couple of older jobs
      // still running. The demob attestation names this company, so a real job
      // should not be left empty. External truck requests stay blank.
      entity: "Volk",
      department: [],
      projectManagers: [],
      dateAwarded: null,
      estimatedStartDate: null,
      estimatedCompletionDate: null,
      estimator: "",
      client: null,
      clients: [],
      loadingClients: false,
      // scope and budgets
      contractValue: 0,
      manDays: 0,
      subcontractorValue: 0,
      scopeBreakdownNote: "",
      totalBudget: 0,
      totalBudgetNote: "",
      laborBudget: 0,
      laborBudgetNote: "",
      dumpingBudget: 0,
      dumpingBudgetNote: "",
      materialBudget: 0,
      materialBudgetNote: "",
      consumablesBudget: 0,
      consumablesBudgetAlert: 0,
      // submitting
      submitting: false,
      snack: false,
      snackColor: "",
      snackText: "",
      submitted: false,
      ignoreLunchRule: false,
      // coordinates finding
      findingCoordinates: false,
      foundCoordinates: null,
      coordinateSearchAttempted: false,
      searchedAddress: "",
    };
  },
  async created() {
    // Estimator + PM selectors: active Project Manager & Office users, sorted A–Z
    this.pmList = await utils.getJobAssignees();
  },
  computed: {
    ...mapState(["user", "jobSites"]),
    isExternalTruckRequest() {
      return this.department.includes("External Truck Request");
    },
    computedDateFormattedMomentjsAwarded() {
      return this.dateAwarded
        ? moment(this.dateAwarded).format("M/DD/YYYY")
        : "";
    },
    computedDateFormattedMomentjsStart() {
      return this.estimatedStartDate
        ? moment(this.estimatedStartDate).format("M/DD/YYYY")
        : "";
    },
    computedDateFormattedMomentjsEnd() {
      return this.estimatedCompletionDate
        ? moment(this.estimatedCompletionDate).format("M/DD/YYYY")
        : "";
    },
  },
  methods: {
    // Geocode address using OpenStreetMap Nominatim
    async geocodeAddress(address) {
      try {
        // Construct full address string
        const addressString = `${address.street || ""} ${address.street2 ||
          ""} ${address.city || ""} ${address.state ||
          ""} ${address.postalCode || ""}`.trim();

        if (!addressString) {
          console.log("No address provided, skipping geocoding");
          return null;
        }

        console.log("Geocoding address:", addressString);

        // Using OpenStreetMap Nominatim (free, no API key required)
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?` +
            new URLSearchParams({
              q: addressString,
              format: "json",
              limit: 1,
            }),
          {
            headers: {
              "User-Agent": "Volk Industrial Services Jobsite App",
            },
          }
        );

        const data = await response.json();

        if (data && data.length > 0) {
          const result = data[0];
          console.log("Geocoding successful:", result.lat, result.lon);
          return {
            latitude: parseFloat(result.lat),
            longitude: parseFloat(result.lon),
          };
        } else {
          console.log("No geocoding results found");
          return null;
        }
      } catch (error) {
        console.error("Geocoding error:", error);
        return null;
      }
    },

    async onDepartmentChange() {
      if (this.isExternalTruckRequest && this.clients.length === 0) {
        await this.loadClients();
      }
      // Clear client if External Truck Request is deselected
      if (!this.isExternalTruckRequest) {
        this.client = null;
      }
      // External truck request jobs do not belong to an operating entity.
      if (this.isExternalTruckRequest) {
        this.entity = "";
      } else if (!this.entity) {
        this.entity = "Volk";
      }
    },

    async loadClients() {
      try {
        this.loadingClients = true;
        const clientsRes = await fb.db
          .collection("clients")
          .orderBy("name", "asc")
          .get();

        this.clients = [];
        clientsRes.forEach((r) => {
          this.clients.push({
            id: r.id,
            ...r.data(),
          });
        });
        this.loadingClients = false;
      } catch (error) {
        console.error("Error loading clients:", error);
        this.loadingClients = false;
      }
    },

    async addJob() {
      try {
        if (this.$refs.form.validate()) {
          this.submitting = true;
          this.submitted = true;

          // Build the job site object
          const jobData = {
            street: this.street,
            street2: this.street2 || "",
            city: this.city,
            state: this.state,
            postalCode: this.postalCode,
            address: `${this.street} ${this.street2 + " "}${this.city}, ${
              this.state
            } ${this.postalCode}`,
            department: this.department,
            projectManagers: this.projectManagers,
            jobId: this.jobId || "",
            proposalNumber: this.proposalNumber || "",
            entity: this.entity || "",
            dateAwarded: this.dateAwarded || null,
            estimatedStartDate: this.estimatedStartDate || null,
            estimatedCompletionDate: this.estimatedCompletionDate || null,
            estimator: this.estimator || "",
            // scope and budgets
            manDays: this.manDays || 0,
            contractValue: this.contractValue || 0,
            subcontractorValue: this.subcontractorValue || 0,
            scopeBreakdownNote: this.scopeBreakdownNote || "",
            totalBudget: this.totalBudget || 0,
            totalBudgetNote: this.totalBudgetNote || "",
            laborBudget: this.laborBudget || 0,
            laborBudgetNote: this.laborBudgetNote || "",
            dumpingBudget: this.dumpingBudget || 0,
            dumpingBudgetNote: this.dumpingBudgetNote || "",
            materialBudget: this.materialBudget || 0,
            materialBudgetNote: this.materialBudgetNote || "",
            consumablesBudget: this.consumablesBudget || 0,
            consumablesBudgetAlert: this.consumablesBudgetAlert || 0,
            status: "open",
            // getJobSites loads only external == false, so every job needs it
            external: this.isExternalTruckRequest,
            createdBy: this.user,
            createDate: fb.timestamp,
            ignoreLunchRule: this.ignoreLunchRule,
          };

          // Add geolocation GeoPoint if coordinates were found via the "Find Coordinates" button
          // Skip for External Truck Request department
          const isExternalTruckRequest = this.department.some((d) =>
            d.toLowerCase().includes("external truck request")
          );

          if (this.foundCoordinates && !isExternalTruckRequest) {
            jobData.geolocation = new firebase.firestore.GeoPoint(
              this.foundCoordinates.latitude,
              this.foundCoordinates.longitude
            );
            console.log(
              "Geolocation added to job site:",
              this.foundCoordinates
            );
          } else if (!isExternalTruckRequest) {
            console.warn(
              "No coordinates found - job site created without geolocation"
            );
          }

          // Add client if External Truck Request is selected
          if (this.isExternalTruckRequest && this.client) {
            jobData.client = {
              id: this.client.id,
              name: this.client.name,
            };
          }

          // Save to Firestore
          await fb.db.collection("jobSites").add(jobData);

          this.$refs.form.reset();
          this.snack = true;
          this.snackColor = "success";
          this.snackText = "Successfully created job!";
          this.submitting = false;
        }
      } catch (error) {
        alert("Error occured creating job.");
        this.submitting = false;
        console.log(error);
      }
    },
    updatePmList() {
      this.pmList = this.pmList.map((pm) => {
        return {
          ...pm,
          notifications: {
            toolOrders: true,
            toolPickups: true,
            consumablesBudget: true,
          },
        };
      });
    },
    dupeJobIdCheck() {
      if (this.jobId && this.jobSites.length && this.jobId.length == 7) {
        return this.jobSites.some((j) => {
          return j.jobId === this.jobId && j.id !== this.$route.params.id;
        });
      }
    },
    async findCoordinates() {
      this.findingCoordinates = true;
      this.coordinateSearchAttempted = false; // Reset before starting
      this.foundCoordinates = null; // Clear any previous results

      try {
        const coords = await this.geocodeAddress({
          street: this.street,
          street2: this.street2,
          city: this.city,
          state: this.state,
          postalCode: this.postalCode,
        });

        const addressString = `${this.street || ""} ${this.street2 || ""} ${this
          .city || ""} ${this.state || ""} ${this.postalCode || ""}`.trim();
        this.searchedAddress = addressString;

        if (coords) {
          this.foundCoordinates = coords;
          this.coordinateSearchAttempted = true;
        } else {
          this.foundCoordinates = null;
          this.coordinateSearchAttempted = true;
        }
      } catch (error) {
        console.error("Error finding coordinates:", error);
        this.foundCoordinates = null;
        this.searchedAddress = "Error occurred during geocoding";
        this.coordinateSearchAttempted = true;
      } finally {
        this.findingCoordinates = false;
      }
    },
    clearCoordinates() {
      this.foundCoordinates = null;
      this.coordinateSearchAttempted = false;
      this.searchedAddress = "";
    },
  },
};
</script>
